import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Fuel, Wrench, MapPin, Receipt } from 'lucide-react'
import { useOperationalExpenses } from '@/hooks/useOperationalExpenses'
import { useCashCategories } from '@/hooks/useCashCategories'
import { useCashAccounts } from '@/hooks/useCashAccounts'
import { useVehicles } from '@/hooks/useVehicles'
import { DataTable } from '@/components/ui/data-table'
import { SearchInput } from '@/components/ui/search-input'
import { Pagination } from '@/components/ui/pagination'
import { Button } from '@/components/ui/button'
import { formatBRL } from '@/lib/currency'
import { cn } from '@/lib/utils'
import { OperationalExpenseModal } from './OperationalExpenseModal'
import type { OperationalExpense } from '@/types/operational-expense'
import type { Column } from '@/components/ui/data-table'

const selectClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
)

type Preset = 'Combustível' | 'Manutenção' | 'Pedágio' | null

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR')
}

const COLUMNS: Column<OperationalExpense>[] = [
  { key: 'date', header: 'Data', render: (row) => formatDate(row.date) },
  { key: 'code', header: 'Código', render: (row) => `E-${row.code}` },
  { key: 'description', header: 'Descrição' },
  {
    key: 'category',
    header: 'Categoria',
    className: 'hidden md:table-cell',
    render: (row) => row.category.name,
  },
  {
    key: 'vehicle',
    header: 'Veículo',
    className: 'hidden lg:table-cell',
    render: (row) => row.vehicle?.plate ?? '—',
  },
  {
    key: 'freight',
    header: 'Frete',
    className: 'hidden lg:table-cell',
    render: (row) => (row.freight ? `F-${row.freight.code}` : '—'),
  },
  {
    key: 'amount',
    header: 'Valor',
    render: (row) => (
      <span className="font-medium tabular-nums text-red-700">−{formatBRL(row.amount)}</span>
    ),
  },
]

export function OperationalExpenseListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [accountId, setAccountId] = useState('')
  const [vehicleId, setVehicleId] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [preset, setPreset] = useState<Preset>(null)

  const params = {
    ...(search && { search }),
    ...(categoryId && { categoryId }),
    ...(accountId && { accountId }),
    ...(vehicleId && { vehicleId }),
    ...(startDate && { startDate: new Date(startDate).toISOString() }),
    ...(endDate && { endDate: new Date(`${endDate}T23:59:59`).toISOString() }),
  }

  const { data, isLoading } = useOperationalExpenses({ ...params, page })
  const { data: categoriesData } = useCashCategories({
    kind: 'OUTFLOW',
    active: true,
    perPage: 100,
  })
  const { data: accountsData } = useCashAccounts({ active: true, perPage: 100 })
  const { data: vehiclesData } = useVehicles({ active: true, perPage: 100 })

  const categories = categoriesData?.data ?? []
  const accounts = accountsData?.data ?? []
  const vehicles = vehiclesData?.data ?? []

  function openWith(p: Preset) {
    setPreset(p)
    setModalOpen(true)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Despesas operacionais</h1>
          <p className="text-sm text-muted-foreground">
            Combustível, manutenção, pedágio e demais saídas operacionais — postadas no caixa.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => openWith('Combustível')}>
            <Fuel size={16} />
            Combustível
          </Button>
          <Button variant="outline" onClick={() => openWith('Manutenção')}>
            <Wrench size={16} />
            Manutenção
          </Button>
          <Button variant="outline" onClick={() => openWith('Pedágio')}>
            <MapPin size={16} />
            Pedágio
          </Button>
          <Button onClick={() => openWith(null)}>
            <Plus size={16} />
            Outra despesa
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Receipt size={16} />
          <span className="text-xs font-medium">Total no filtro atual</span>
        </div>
        <p className="mt-2 text-xl font-semibold tabular-nums text-red-700">
          −{formatBRL(data?.totalAmount ?? 0)}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <SearchInput
          value={search}
          onChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder="Buscar…"
          className="lg:col-span-2"
        />
        <select
          className={selectClass}
          value={categoryId}
          onChange={(e) => {
            setCategoryId(e.target.value)
            setPage(1)
          }}
        >
          <option value="">Todas as categorias</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          className={selectClass}
          value={accountId}
          onChange={(e) => {
            setAccountId(e.target.value)
            setPage(1)
          }}
        >
          <option value="">Todas as contas</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        <select
          className={selectClass}
          value={vehicleId}
          onChange={(e) => {
            setVehicleId(e.target.value)
            setPage(1)
          }}
        >
          <option value="">Todos os veículos</option>
          {vehicles.map((v) => (
            <option key={v.id} value={v.id}>{v.plate}</option>
          ))}
        </select>
        <input
          type="date"
          className={selectClass}
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value)
            setPage(1)
          }}
          aria-label="Data inicial"
        />
        <input
          type="date"
          className={selectClass}
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value)
            setPage(1)
          }}
          aria-label="Data final"
        />
      </div>

      <DataTable
        columns={COLUMNS}
        data={data?.data ?? []}
        keyExtractor={(row) => row.id}
        onRowClick={(row) => navigate(`/operational-expenses/${row.id}`)}
        isLoading={isLoading}
        emptyTitle="Nenhuma despesa cadastrada"
        emptyDescription="Registre combustível, manutenção, pedágio e outras saídas operacionais."
        emptyAction={
          <Button onClick={() => openWith(null)}>
            <Plus size={16} />
            Nova despesa
          </Button>
        }
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          total={data.total}
          perPage={data.perPage}
          onChange={setPage}
        />
      )}

      <OperationalExpenseModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setPreset(null)
        }}
        presetCategoryName={preset}
      />
    </div>
  )
}
